import { FC, useEffect, useRef, useState } from 'react'
import styled, { keyframes } from 'styled-components'

const gradientAnimation = keyframes`
    0% {
        background-position: 0% 50%;
    }
    50% {
        background-position: 100% 50%;
    }
    100% {
        background-position: 0% 50%;
    }

`

const Track = styled.div<{ $width: string; $height: string }>`
    position: relative;
    width: ${(p) => p.$width};
    height: ${(p) => p.$height};
    background: ${(p) => p.theme.colors.lighterGrey};
    border-radius: calc(${(p) => p.$height} / 2);
`

const Progress = styled(Track)<{ $tint?: string; $skipTransition?: boolean }>`
    height: auto;
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    background: ${(p) =>
        p.$tint
            ? p.$tint
            : 'linear-gradient(-45deg, #37de7f, #25afe1, #23d5ab, #23a6d5)'};
    background-size: 400% 400%;
    animation-name: ${(p) => (p.$tint ? 'none' : gradientAnimation)};
    animation-duration: 10s;
    animation-iteration-count: infinite;
    transition: ${(p) =>
        p.$skipTransition ? 'none' : 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)'};
`

interface Props {
    width?: string
    height?: string
    progress: number
    /** Enable trickle animation between real updates */
    trickle?: boolean
    /** Trickle interval in ms (default: 800) */
    trickleInterval?: number
    /** Override the default gradient with a solid/tinted color */
    tint?: string
    /** When true, disable the width transition (snap to new position) */
    skipTransition?: boolean
}

export const ProgressBar: FC<Props> = ({
    width = '100%',
    height = '10px',
    progress,
    trickle = true,
    trickleInterval = 800,
    tint,
    skipTransition = false,
}) => {
    // Clamp to [0, 1] — fast-pipeline completion intentionally sets
    // progress to 1.0 for a full-bar animation.
    const clampedProgress = Math.min(Math.max(progress, 0), 1)

    const [displayProgress, setDisplayProgress] = useState(clampedProgress)
    const targetRef = useRef(clampedProgress)

    // When the real progress changes, snap to it
    useEffect(() => {
        targetRef.current = clampedProgress
        setDisplayProgress(clampedProgress)
    }, [clampedProgress])

    // Trickle: slowly increment between real updates
    useEffect(() => {
        if (!trickle || clampedProgress >= 1) return

        const interval = setInterval(() => {
            setDisplayProgress((prev) => {
                const target = targetRef.current
                // Don't trickle past the next expected milestone (target + gap to next step)
                // Use diminishing increments so it slows down as it approaches the target
                const ceiling = Math.min(target + 0.08, 0.95)
                const remaining = ceiling - prev
                if (remaining <= 0.005) return prev
                // Faster trickle rate at shorter intervals for snappier feel
                const rate = trickleInterval <= 400 ? 0.15 : 0.08
                const increment = remaining * rate
                return Math.min(prev + increment, 0.95)
            })
        }, trickleInterval)

        return () => clearInterval(interval)
    }, [trickle, trickleInterval, clampedProgress])

    return (
        <Track $width={width} $height={height}>
            <Progress
                $width={`calc(${width} * ${displayProgress})`}
                $height={height}
                $tint={tint}
                $skipTransition={skipTransition}
            />
        </Track>
    )
}
